import { Button, Input, Label, PaguzLogo } from '@paguz/ui';
import { useMutation } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { LockKeyholeIcon, ShieldCheckIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { ApiError } from '../../api/client.ts';
import { api } from '../../api/endpoints.ts';
import { appConfig } from '../../config.ts';
import { MOCK_PASSWORD } from '../../mocks/seed-data.ts';
import { sessionStore } from '../../lib/session.ts';

const route = getRouteApi('/login');

export function LoginPage() {
  const { redirect } = route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = useState(appConfig.useMocks ? 'admin@demo.paguz.com' : '');
  const [password, setPassword] = useState('');
  const emailId = useId();
  const passwordId = useId();

  const login = useMutation({
    mutationFn: () => api.login(email.trim(), password),
    onSuccess: async ({ accessToken, user }) => {
      sessionStore.start(accessToken, user);
      await navigate({ to: redirect ?? '/' });
    },
  });

  const errorMessage =
    login.error instanceof ApiError && login.error.status === 401
      ? 'Correo o contraseña incorrectos.'
      : login.error
        ? 'No pudimos iniciar sesión. Intenta de nuevo.'
        : null;

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-inverse p-12 text-inverse-foreground lg:flex">
        <PaguzLogo tone="white" className="h-9 w-auto self-start" />
        <div className="relative z-10 max-w-md">
          <p className="text-sm font-semibold tracking-wider text-violet-light uppercase">
            Consola back-office
          </p>
          <h1 className="mt-3 font-display text-4xl leading-tight font-semibold">
            Opera el crédito rotativo conectado a Bre-B, con trazabilidad total.
          </h1>
          <ul className="mt-8 flex flex-col gap-3 text-inverse-foreground/85">
            <li className="flex gap-3">
              <ShieldCheckIcon className="size-5 shrink-0 text-violet-light" /> Cada acción sensible
              pide doble confirmación y motivo, y queda en auditoría.
            </li>
            <li className="flex gap-3">
              <ShieldCheckIcon className="size-5 shrink-0 text-violet-light" /> Ves solo las
              secciones de tu rol; el servidor valida cada permiso.
            </li>
          </ul>
        </div>
        <p className="relative z-10 text-sm text-inverse-muted-foreground">
          {appConfig.tenant.displayName} · {appConfig.tenant.environment}
        </p>
        <div
          aria-hidden="true"
          className="absolute -right-32 -bottom-32 size-[28rem] rounded-full bg-violet-600/30 blur-3xl"
        />
      </section>

      <section className="flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm">
          <PaguzLogo className="mb-8 h-8 w-auto lg:hidden" />
          <h2 className="font-display text-3xl font-semibold text-primary">Ingresa a la consola</h2>
          <p className="mt-1 text-muted-foreground">Usa tu correo corporativo y tu contraseña.</p>

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              login.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={emailId}>Correo</Label>
              <Input
                id={emailId}
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={passwordId}>Contraseña</Label>
              <Input
                id={passwordId}
                type="password"
                autoComplete="current-password"
                required
                minLength={8}
                value={password}
                aria-invalid={errorMessage ? true : undefined}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </div>
            {errorMessage && (
              <p
                role="alert"
                className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-foreground"
              >
                {errorMessage}
              </p>
            )}
            <Button type="submit" variant="highlight" size="lg" loading={login.isPending}>
              <LockKeyholeIcon /> Ingresar
            </Button>
          </form>

          {appConfig.useMocks && (
            <div className="mt-8 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
              <p className="font-semibold text-foreground">Modo demo sin API (MSW)</p>
              <p className="mt-1">
                Usuarios <code>admin@</code>, <code>analista@</code>, <code>operador@</code>,{' '}
                <code>conocimiento@</code> y <code>auditor@demo.paguz.com</code> con la contraseña{' '}
                <code className="font-mono text-foreground">{MOCK_PASSWORD}</code>.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
